import { useState } from "react"
import type { Meeting } from "@/schema"
import { Button, Skeleton } from "@ui/shad"
import { Popover, PopoverContent, PopoverTrigger } from "@ui/shad/popover"
import { Plus } from "lucide-react"

import { useFetchFeatureFlags } from "@/hooks/api/feature-flags/queries"
import { useMeetingTopics } from "@/hooks/api/meeting-topics"

import MeetingTopicForm from "./meeting-topic-form"

export default function MeetingTopicSelector({ meeting }: { meeting: Meeting }) {
  const featureFlags = useFetchFeatureFlags()
  const enabled = featureFlags.data?.includes("meeting_topics") ?? false
  const { data, isLoading } = useMeetingTopics({ enabled })
  const [open, setOpen] = useState(false)
  const selected = meeting.meeting_topics ?? []
  const visibleTopics = selected.slice(0, 4)
  const hiddenCount = Math.max(selected.length - visibleTopics.length, 0)

  if (!enabled) return null
  if (isLoading || !data) return <Skeleton className="h-6 w-28" />

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="flex min-w-0 flex-wrap items-center gap-1">
          {visibleTopics.map((topic) => (
            <Button key={topic.id} variant="secondary" size="xs">
              {topic.name}
            </Button>
          ))}
          {hiddenCount > 0 && (
            <Button variant="secondary" size="xs">
              +{hiddenCount} more
            </Button>
          )}
          <Button variant="ghost" size="xs">
            <Plus />
            Add topics
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-fit p-0" align="start">
        <MeetingTopicForm meeting={meeting} availableTopics={data.meeting_topics} />
      </PopoverContent>
    </Popover>
  )
}
